import { useCallback, useEffect, useState } from 'react'
import { Trash2, Plus } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { useToast } from '@/providers/UiProviders'
import { ErrorState, Skeleton, EmptyState } from '@/components/States'
import { ApiError } from '@/lib/api'
import {
  fetchBannedCategories,
  addBannedCategory,
  deleteBannedCategory,
  fetchChannelRevenue,
  fetchComplaints,
  resolveComplaint,
  fetchAdStats,
  fetchPolicy,
  savePolicy,
  ADSX_STR as S,
  type BannedCategory,
  type ChannelRevenue,
  type Complaint,
  type AdStats,
} from './adsExtraApi'

function useLoad<T>(fn: (s: AbortSignal) => Promise<T>) {
  const [data, setData] = useState<T | null>(null)
  const [err, setErr] = useState<ApiError | null>(null)
  const [tick, setTick] = useState(0)
  useEffect(() => {
    const ac = new AbortController()
    setErr(null)
    fn(ac.signal)
      .then(setData)
      .catch((e) => {
        if (!ac.signal.aborted) setErr(e instanceof ApiError ? e : new ApiError('INTERNAL', String(e), 500))
      })
    return () => ac.abort()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tick])
  return { data, err, reload: useCallback(() => setTick((t) => t + 1), []) }
}

function Loading({ n = 8 }: { n?: number }) {
  return (
    <div className="space-y-1.5 p-4">
      {Array.from({ length: n }).map((_, i) => (
        <Skeleton key={i} className="h-7 w-full" />
      ))}
    </div>
  )
}

/* ── A20.06 — Taqiqlangan kategoriyalar ────────────────────────── */
export function AdsBannedCategoriesScreen() {
  const { toast } = useToast()
  const { data, err, reload } = useLoad<BannedCategory[]>(fetchBannedCategories)
  const [cat, setCat] = useState('')
  const [busy, setBusy] = useState(false)

  const add = async () => {
    const v = cat.trim()
    if (!v) return
    setBusy(true)
    try {
      await addBannedCategory(v)
      setCat('')
      toast(S.done, { tone: 'success' })
      reload()
    } catch {
      toast(S.failed, { tone: 'error' })
    } finally {
      setBusy(false)
    }
  }
  const del = async (id: number) => {
    try {
      await deleteBannedCategory(id)
      reload()
    } catch {
      toast(S.failed, { tone: 'error' })
    }
  }

  return (
    <>
      <PageHeader title={S.banned} screenId="A20.06" description={S.bannedDesc} />
      <div className="min-h-0 flex-1 overflow-auto p-4">
        <div className="mx-auto max-w-2xl">
          <div className="mb-3 flex gap-2">
            <input
              value={cat}
              onChange={(e) => setCat(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && add()}
              placeholder={S.categoryPrompt}
              className="flex-1 rounded-md border border-border-default bg-surface-1 px-3 py-2 text-sm text-primary outline-none focus:border-accent"
            />
            <button
              type="button"
              onClick={add}
              disabled={busy || !cat.trim()}
              className="flex items-center gap-1 rounded-md bg-accent px-3 py-2 text-sm text-white disabled:opacity-50"
            >
              <Plus size={14} />
              {S.add}
            </button>
          </div>
          {err ? (
            <ErrorState code={err.code} onRetry={reload} />
          ) : !data ? (
            <Loading />
          ) : data.length === 0 ? (
            <EmptyState title={S.empty} />
          ) : (
            <div className="overflow-hidden rounded-lg border border-border-default">
              {data.map((c, i) => (
                <div
                  key={c.id}
                  className={`flex items-center justify-between px-4 py-2.5 ${i > 0 ? 'border-t border-border-subtle' : ''}`}
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm text-primary">{c.category}</p>
                    {c.note && <p className="truncate text-xs text-tertiary">{c.note}</p>}
                  </div>
                  <button
                    type="button"
                    onClick={() => del(c.id)}
                    className="shrink-0 rounded-md p-1.5 text-tertiary hover:bg-surface-3 hover:text-danger"
                    title={S.delete}
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  )
}

/* ── A20.11 — Kampaniya statistikasi ───────────────────────────── */
function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-border-default bg-surface-2 p-4">
      <p className="text-xs text-tertiary">{label}</p>
      <p className="mt-1 text-2xl font-semibold text-primary tabular">{value}</p>
    </div>
  )
}
export function AdsStatsScreen() {
  const { data, err, reload } = useLoad<AdStats>(fetchAdStats)
  return (
    <>
      <PageHeader title={S.stats} screenId="A20.11" />
      <div className="min-h-0 flex-1 overflow-auto p-4">
        {err ? (
          <ErrorState code={err.code} onRetry={reload} />
        ) : !data ? (
          <Loading />
        ) : (
          <div className="mx-auto max-w-4xl">
            <div className="grid gap-2.5 sm:grid-cols-3 lg:grid-cols-6">
              <StatCard label={S.advertisers} value={data.advertisers.toLocaleString()} />
              <StatCard label={S.activeCampaigns} value={data.active_campaigns.toLocaleString()} />
              <StatCard label={S.totalSpend} value={data.total_spend.toLocaleString()} />
              <StatCard label={S.impressions} value={data.impressions.toLocaleString()} />
              <StatCard label={S.clicks} value={data.clicks.toLocaleString()} />
              <StatCard label={S.ctr} value={`${data.ctr.toFixed(2)}%`} />
            </div>
            {data.top_campaigns && data.top_campaigns.length > 0 && (
              <div className="mt-4 overflow-hidden rounded-lg border border-border-default">
                <p className="border-b border-border-subtle bg-surface-2 px-4 py-2 text-xs font-medium text-secondary">
                  {S.topCampaigns}
                </p>
                {data.top_campaigns.map((t, i) => (
                  <div
                    key={i}
                    className={`flex items-center justify-between px-4 py-2 text-sm ${i > 0 ? 'border-t border-border-subtle' : ''}`}
                  >
                    <span className="truncate text-primary">{t.title}</span>
                    <span className="shrink-0 tabular text-secondary">{t.spent.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  )
}

/* ── A20.15 — Kanal daromadi taqsimoti ─────────────────────────── */
export function AdsChannelRevenueScreen() {
  const { data, err, reload } = useLoad<ChannelRevenue[]>(fetchChannelRevenue)
  return (
    <>
      <PageHeader title={S.channelRev} screenId="A20.15" />
      <div className="min-h-0 flex-1 overflow-auto p-4">
        {err ? (
          <ErrorState code={err.code} onRetry={reload} />
        ) : !data ? (
          <Loading />
        ) : data.length === 0 ? (
          <EmptyState title={S.empty} />
        ) : (
          <div className="mx-auto max-w-3xl overflow-hidden rounded-lg border border-border-default">
            <table className="w-full text-left text-sm">
              <thead className="bg-surface-2 text-xs text-tertiary">
                <tr>
                  <th className="px-4 py-2 font-medium">{S.channel}</th>
                  <th className="px-4 py-2 text-right font-medium">{S.impressions}</th>
                  <th className="px-4 py-2 text-right font-medium">{S.revenue}</th>
                  <th className="px-4 py-2 text-right font-medium">{S.updated}</th>
                </tr>
              </thead>
              <tbody>
                {data.map((c, i) => (
                  <tr key={i} className="border-t border-border-subtle">
                    <td className="px-4 py-2 font-mono text-primary">{c.channel_ref}</td>
                    <td className="px-4 py-2 text-right tabular text-secondary">{c.impressions.toLocaleString()}</td>
                    <td className="px-4 py-2 text-right tabular text-secondary">{c.revenue.toLocaleString()}</td>
                    <td className="px-4 py-2 text-right text-tertiary">{c.updated_at}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  )
}

/* ── A20.16 — Reklama shikoyatlari ─────────────────────────────── */
export function AdsComplaintsScreen() {
  const { toast } = useToast()
  const { data, err, reload } = useLoad<Complaint[]>((s) => fetchComplaints('', s))
  const [busy, setBusy] = useState<number | null>(null)

  const act = async (id: number, action: 'resolve' | 'dismiss') => {
    setBusy(id)
    try {
      await resolveComplaint(id, action)
      toast(S.done, { tone: 'success' })
      reload()
    } catch {
      toast(S.failed, { tone: 'error' })
    } finally {
      setBusy(null)
    }
  }

  return (
    <>
      <PageHeader title={S.complaints} screenId="A20.16" />
      <div className="min-h-0 flex-1 overflow-auto p-4">
        {err ? (
          <ErrorState code={err.code} onRetry={reload} />
        ) : !data ? (
          <Loading />
        ) : data.length === 0 ? (
          <EmptyState title={S.empty} />
        ) : (
          <div className="mx-auto max-w-3xl space-y-2">
            {data.map((c) => (
              <div key={c.id} className="rounded-lg border border-border-default bg-surface-2 p-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm text-primary">{c.reason || `#${c.id}`}</p>
                    <p className="mt-0.5 font-mono text-xs text-tertiary">
                      camp {c.campaign_id} · creative {c.creative_id} · {c.status} · {c.created_at}
                    </p>
                  </div>
                  {c.status === 'open' || c.status === 'pending' ? (
                    <div className="flex shrink-0 gap-1.5">
                      <button
                        type="button"
                        disabled={busy === c.id}
                        onClick={() => act(c.id, 'resolve')}
                        className="rounded-md bg-accent px-2.5 py-1 text-xs text-white disabled:opacity-50"
                      >
                        {S.resolve}
                      </button>
                      <button
                        type="button"
                        disabled={busy === c.id}
                        onClick={() => act(c.id, 'dismiss')}
                        className="rounded-md border border-border-default px-2.5 py-1 text-xs text-secondary hover:text-primary disabled:opacity-50"
                      >
                        {S.dismiss}
                      </button>
                    </div>
                  ) : (
                    <span className="shrink-0 text-xs text-tertiary">{c.status}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  )
}

/* ── A20.20 — Reklama siyosati ─────────────────────────────────── */
export function AdsPolicyScreen() {
  const { toast } = useToast()
  const { data, err, reload } = useLoad<string>(fetchPolicy)
  const [text, setText] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const value = text ?? data ?? ''

  const save = async () => {
    setBusy(true)
    try {
      await savePolicy(value)
      toast(S.saved, { tone: 'success' })
      setText(null)
      reload()
    } catch {
      toast(S.failed, { tone: 'error' })
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <PageHeader
        title={S.policy}
        screenId="A20.20"
        description={S.policyDesc}
        actions={
          <button
            type="button"
            onClick={save}
            disabled={busy || data === null}
            className="rounded-md bg-accent px-3 py-1 text-sm text-white disabled:opacity-50"
          >
            {S.save}
          </button>
        }
      />
      <div className="min-h-0 flex-1 overflow-auto p-4">
        {err ? (
          <ErrorState code={err.code} onRetry={reload} />
        ) : data === null ? (
          <Loading n={10} />
        ) : (
          <textarea
            value={value}
            onChange={(e) => setText(e.target.value)}
            spellCheck={false}
            className="mx-auto block h-[60vh] w-full max-w-3xl rounded-lg border border-border-default bg-surface-1 p-4 font-mono text-sm text-primary outline-none focus:border-accent"
          />
        )}
      </div>
    </>
  )
}
